import { useMemo, useState } from "react";
import { ArrowLeft, Plus, Search as SearchIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { FieldOption, FilterCondition } from "./AdvancedFilters";
import { ChipEditor, defaultOperatorFor } from "./FilterChip";
import type { PipelineStatus, ProjectMember } from "@/api/types";

interface AddFilterPopoverProps {
  fields: FieldOption[];
  slug: string;
  members: ProjectMember[];
  pipelineStatuses: PipelineStatus[];
  /** Confirm callback. Parent decides how to route the new
   *  condition: pipeline_status merges into the pills row, score
   *  routes to the slider, everything else is appended to the
   *  advanced array. */
  onAdd: (next: {
    field: string;
    operator: string;
    value: unknown;
  }) => void;
  /** Visual style — chip-bar variant is the dashed "+ Add filter"
   *  inline at the end of the chips bar; toolbar variant is the
   *  standalone button shown when zero chips are active. */
  variant?: "chip-bar" | "toolbar";
}

/** Two-step popover that lets a user pick a field then choose its
 *  operator + value. On confirm, hands the spec back to the parent
 *  which decides routing per source. */
export function AddFilterPopover({
  fields,
  slug,
  members,
  pipelineStatuses,
  onAdd,
  variant = "chip-bar",
}: AddFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"pick-field" | "edit-value">("pick-field");
  const [selectedField, setSelectedField] = useState<FieldOption | null>(null);
  const [search, setSearch] = useState("");

  function reset() {
    setStep("pick-field");
    setSelectedField(null);
    setSearch("");
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      // Defer the inner reset by one tick so the closing animation
      // doesn't flash to step 1 mid-collapse. Radix unmounts on
      // close, so a microtask is sufficient.
      queueMicrotask(reset);
    }
  }

  const filteredFields = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return fields;
    return fields.filter((f) => f.label.toLowerCase().includes(q));
  }, [fields, search]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {variant === "chip-bar" ? (
          <button
            type="button"
            aria-label="Add a new filter"
            className={cn(
              // Same padding as a chip (px-2.5 py-1) so the trailing
              // affordance sits flush with the chip row's baseline.
              "inline-flex items-center gap-1.5 rounded-full border border-dashed px-2.5 py-1 text-xs transition-colors",
              "border-slate-400 bg-white text-slate-600",
              "hover:border-slate-500 hover:bg-slate-50 hover:text-slate-700",
              "dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300",
              "dark:hover:border-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-100",
            )}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add filter
          </button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label="Add a new filter"
            className="text-slate-700"
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add filter
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-[380px] p-0"
        // Don't snap the focus to the back button when transitioning
        // to step 2 — let the operator dropdown handle its own
        // focus when the user opens it.
        onOpenAutoFocus={(e) => {
          // Step 1: focus the search input. Default behaviour does
          // this; only override on step 2 to avoid stealing focus.
          if (step !== "pick-field") e.preventDefault();
        }}
      >
        {step === "pick-field" ? (
          <FieldPicker
            fields={filteredFields}
            search={search}
            onSearch={setSearch}
            onPick={(f) => {
              setSelectedField(f);
              setStep("edit-value");
            }}
          />
        ) : selectedField ? (
          <div className="p-3">
            <button
              type="button"
              onClick={reset}
              className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-700"
            >
              <ArrowLeft className="h-3 w-3" />
              Back
            </button>
            <ChipEditor
              field={selectedField}
              initial={initialConditionFor(selectedField)}
              slug={slug}
              members={members}
              pipelineStatuses={pipelineStatuses}
              onCancel={() => handleOpenChange(false)}
              onConfirm={(next) => {
                onAdd({
                  field: selectedField.value,
                  operator: next.operator,
                  value: next.value,
                });
                handleOpenChange(false);
              }}
            />
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function FieldPicker({
  fields,
  search,
  onSearch,
  onPick,
}: {
  fields: FieldOption[];
  search: string;
  onSearch: (next: string) => void;
  onPick: (f: FieldOption) => void;
}) {
  return (
    <div className="flex flex-col">
      <div className="relative border-b border-slate-900/[0.06] p-2">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          autoFocus
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search attributes…"
          className="h-8 border-slate-200 pl-8 text-xs"
        />
      </div>
      <div className="max-h-72 overflow-y-auto py-1">
        {fields.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-slate-400">
            No attributes match.
          </p>
        ) : (
          <ul>
            {fields.map((f) => (
              <li key={f.value}>
                <button
                  type="button"
                  onClick={() => onPick(f)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <span>{f.label}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">
                    {f.type}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Seed a fresh FilterCondition for the chosen field. Mirrors
 *  AdvancedFilters' `emptyCondition` shape (default operator,
 *  appropriate empty value). */
function initialConditionFor(field: FieldOption): FilterCondition {
  const op = defaultOperatorFor(field.type);
  return {
    id: "add-filter-draft",
    field: field.value,
    operator: op,
    value:
      op === "between"
        ? ["", ""]
        : op === "in" || op === "not_in"
          ? []
          : field.type === "boolean"
            ? false
            : "",
  };
}
